import { useState, useRef, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Progress } from "@/components/ui/progress.jsx";
import { useToast } from "@/hooks/use-toast.js";
import {
  Upload,
  Check,
  X,
  FileText,
  Loader2,
  Trash2,
  ChevronDown,
  RefreshCw,
  Hourglass, // Added for the pending card
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog.jsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.jsx";
import { api } from "@/lib/api/api";

const BankStatements = ({
  companyId,
  documentsData,
  banks, // Integrated from the banks endpoint
  isLoadingDocuments,
  documentsError,
  onRefresh,
}) => {
  const { toast } = useToast();
  const [docsCount, setDocsCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingBank, setIsDeletingBank] = useState(false);
  const [isDeletingStatement, setIsDeletingStatement] = useState(false);
  const [openCollapsibles, setOpenCollapsibles] = useState(() => new Set());
  const [uploadSuccessTime, setUploadSuccessTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const docsInputRef = useRef(null);
  const timerRef = useRef(null);

  const onDocsSelect = (e) => {
    const files = e.target.files;
    const count = files?.length ?? 0;
    setDocsCount(count);
    setSelectedFiles(files);
  };

  const uploadDocuments = async () => {
    if (!selectedFiles || !companyId) return;

    const formData = new FormData();
    Array.from(selectedFiles).forEach((file) => {
      formData.append("files", file); // Key 'files' required by API spec
    });

    setIsUploading(true);

    const response = await api.createStatement(companyId, formData);

    if (!response.ok) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue.",
        variant: "destructive",
      });
      setIsUploading(false);
      return;
    }

    const data = await response.json(); // Parse if you need the task_id
    console.log("Upload accepted, task IDs:", data);

    toast({
      title: "Documents téléchargés",
      description: "Les documents ont été téléchargés avec succès.",
    });

    toast({
      title: "Traitement en cours",
      description: "Le traitement des documents a commencé.",
    });

    // Clear form after successful upload
    setSelectedFiles(null);
    setDocsCount(0);
    if (docsInputRef.current) {
      docsInputRef.current.value = "";
    }

    // Start timer for 1.5 minutes (90 seconds)
    const successTime = Date.now();
    setUploadSuccessTime(successTime);
    setTimeRemaining(90);

    // Set up timer
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - successTime) / 1000);
      const remaining = 90 - elapsed;

      if (remaining <= 0) {
        // Timer finished - reload page or refresh data
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setUploadSuccessTime(null);
        setTimeRemaining(null);
        onRefresh();
        // Reload the page to show updated data
        window.location.reload();
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    setIsUploading(false);
  };

  const handleDeleteBank = async (bankId) => {
    setIsDeletingBank(true);

    const response = await api.deleteBankAccount(companyId, bankId);
    if (!response.ok) {
      toast({
        title: "Erreur de suppression",
        description: "Impossible de supprimer le compte bancaire",
        variant: "destructive",
      });
      setIsDeletingBank(false);
      return;
    }

    toast({
      title: "Compte bancaire supprimé",
      description: "La compte bancaire a été supprimé avec succès.",
    });
    onRefresh();
    setIsDeletingBank(false);
  };

  const handleDeleteBankStatement = async (statementId) => {
    setIsDeletingStatement(true);

    const response = await api.deleteStatement(statementId, companyId);
    if (!response.ok) {
      toast({
        title: "Erreur de suppression",
        description: "Impossible de supprimer le relevé bancaire",
        variant: "destructive",
      });
      setIsDeletingStatement(false);
      return;
    }

    toast({
      title: "Relevé bancaire supprimé",
      description: "Le relevé bancaire a été supprimé avec succès.",
    });
    onRefresh();
    setIsDeletingStatement(false);
  };

  const toggleCollapsible = (index) => {
    const newOpenCollapsibles = new Set(openCollapsibles);
    if (newOpenCollapsibles.has(index)) {
      newOpenCollapsibles.delete(index);
    } else {
      newOpenCollapsibles.add(index);
    }
    setOpenCollapsibles(newOpenCollapsibles);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleManualRefresh = () => {
    onRefresh();
    // Optionally reload the page
    window.location.reload();
  };

  const formatTimeRemaining = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Helper function to get document status icon
  const getDocumentStatusIcon = (status) => {
    switch (status) {
      case "ocr_completed":
        return { icon: Check, color: "bg-green-500" };
      case "processing":
        return { icon: Loader2, color: "bg-yellow-500" };
      case "failed":
        return { icon: X, color: "bg-destructive" };
      default:
        // Default to success for existing data if status is missing
        return { icon: Check, color: "bg-green-500" };
    }
  };

  // Helper function to format document date for display
  const formatDocumentDate = (dateString) => {
    if (!dateString) return "Date inconnue";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
    });
  };

  // Render bank statement documents
  const renderBankStatementDocuments = () => {
    if (isLoadingDocuments) {
      return (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Chargement des documents...</span>
        </div>
      );
    }

    if (documentsError) {
      return (
        <div className="flex items-center justify-center p-8 text-destructive">
          <X className="h-6 w-6 mr-2" />
          <span>Erreur lors du chargement des documents</span>
        </div>
      );
    }

    // Show processing message after successful upload
    if (timeRemaining !== null) {
      return (
        <Card>
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Traitement des documents en cours...
                </p>
                <p className="text-sm text-muted-foreground max-w-md">
                  Cela prend généralement 1 à 2 minutes. Nous vous notifierons
                  immédiatement si une action est requise de votre part.
                </p>
                <p className="text-xs text-muted-foreground mt-4">
                  Actualisation automatique dans:{" "}
                  {formatTimeRemaining(timeRemaining)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Identify truly pending files (those without a date assigned yet)
    const pendingFiles = documentsData?.filter((doc) => !doc.date) || [];

    // We use the 'banks' data because its 'cash_flow' is the authoritative copy of processed statements
    const processedBankAccounts = banks || [];

    if (pendingFiles.length === 0 && processedBankAccounts.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* --- Card for Documents Under Analysis (No Date) --- */}
          {pendingFiles.length > 0 && (
            <Card className="border-dashed border-primary/50 bg-primary/5">
              <CardHeader className="pb-3 text-center">
                <CardTitle className="text-sm font-medium flex items-center justify-center gap-2">
                  <Hourglass className="h-4 w-4 text-primary animate-pulse" />
                  Analyses en cours
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {pendingFiles.length} document
                  {pendingFiles.length > 1 ? "s" : ""}
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {pendingFiles.map((file) => (
                  <div key={file.id} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-yellow-500 flex items-center justify-center">
                      <Hourglass className="w-3 h-3 text-white" />
                    </div>
                    <span
                      className="text-sm flex-1 truncate"
                      title={file.file_name}
                    >
                      Document {file.id}
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive h-6 w-6 p-1"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Supprimer le document
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce document en
                            cours d'analyse ?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteBankStatement(file.id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* --- Render Bank Cards using original layout logic --- */}
          {processedBankAccounts.map((bankAccount, index) => {
            // Reconstructing the last 6 months logic as per original UI
            const last6Months = Array.from({ length: 6 }, (_, i) => {
              const d = new Date();
              d.setMonth(d.getMonth() - i);
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
            }).reverse();

            // Use the cash_flow array inside the bank account
            const cashFlow = bankAccount.cash_flow || [];

            // Create a map of existing bank statements by date for quick lookup
            const statementsByDate = new Map();
            cashFlow.forEach((stmt) => {
              const stmtDate = new Date(stmt.date);
              const monthKey = `${stmtDate.getFullYear()}-${String(stmtDate.getMonth() + 1).padStart(2, "0")}-01`;
              statementsByDate.set(monthKey, stmt);
            });

            // Calculate missing documents count
            const existingDocs = last6Months.filter((monthDate) =>
              statementsByDate.has(monthDate),
            ).length;
            const missingDocs = last6Months.length - existingDocs;

            return (
              <Card key={bankAccount.id || index}>
                <CardHeader className="pb-3">
                  <div className="relative">
                    <div className="text-center">
                      <CardTitle className="text-sm font-medium">
                        {bankAccount.iban || "IBAN non détecté"}
                      </CardTitle>
                      <div className="text-xs text-destructive font-medium mt-1">
                        {missingDocs} manquant{missingDocs > 1 ? "s" : ""}
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute top-0 right-0 text-destructive hover:text-destructive hover:bg-destructive/10 p-1 h-6 w-6"
                          disabled={isDeletingBank}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Supprimer le compte bancaire
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce compte ?
                            Cette action est irréversible.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteBank(bankAccount.id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  <div className="space-y-2">
                    {last6Months.map((monthDate) => {
                      const statement = statementsByDate.get(monthDate);

                      if (statement) {
                        // Statement exists - show with status and delete button
                        const { icon: StatusIcon, color } =
                          getDocumentStatusIcon("ocr_completed");
                        return (
                          <div
                            key={statement.id}
                            className="flex items-center gap-2"
                          >
                            <div
                              className={`w-5 h-5 rounded-full ${color} flex items-center justify-center`}
                            >
                              <StatusIcon className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-sm flex-1">
                              {formatDocumentDate(statement.date)}
                            </span>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10 p-1 h-6 w-6"
                                  disabled={isDeletingStatement}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Supprimer le relevé bancaire
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Êtes-vous sûr de vouloir supprimer ce relevé
                                    bancaire ? Cette action est irréversible.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      handleDeleteBankStatement(statement.id)
                                    }
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Supprimer
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        );
                      } else {
                        // No statement for this month - show as missing
                        return (
                          <div
                            key={monthDate}
                            className="flex items-center gap-2"
                          >
                            <div className="w-5 h-5 rounded-full bg-destructive flex items-center justify-center">
                              <X className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {formatDocumentDate(monthDate)} - Non disponible
                            </span>
                          </div>
                        );
                      }
                    })}
                  </div>

                  {/* Collapsible section for older documents */}
                  {(() => {
                    const olderStatements = cashFlow.filter((stmt) => {
                      const stmtDate = new Date(stmt.date);
                      const stmtMonthKey = `${stmtDate.getFullYear()}-${String(stmtDate.getMonth() + 1).padStart(2, "0")}-01`;
                      return !last6Months.includes(stmtMonthKey);
                    });

                    if (olderStatements.length === 0) return null;

                    return (
                      <Collapsible
                        open={openCollapsibles.has(index)}
                        onOpenChange={() => toggleCollapsible(index)}
                      >
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-between text-xs text-muted-foreground hover:text-foreground"
                          >
                            <span>
                              Voir les documents plus anciens (
                              {olderStatements.length})
                            </span>
                            <ChevronDown
                              className={`h-3 w-3 transition-transform ${openCollapsibles.has(index) ? "rotate-180" : ""}`}
                            />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2 mt-2">
                          {olderStatements
                            .sort(
                              (a, b) =>
                                new Date(b.date).getTime() -
                                new Date(a.date).getTime(),
                            )
                            .map((statement) => {
                              const { icon: StatusIcon, color } =
                                getDocumentStatusIcon("ocr_completed");
                              return (
                                <div
                                  key={statement.id}
                                  className="flex items-center gap-2"
                                >
                                  <div
                                    className={`w-5 h-5 rounded-full ${color} flex items-center justify-center`}
                                  >
                                    <StatusIcon className="w-3 h-3 text-white" />
                                  </div>
                                  <span className="text-sm flex-1">
                                    {formatDocumentDate(statement.date)}
                                  </span>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10 p-1 h-6 w-6"
                                        disabled={isDeletingStatement}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          Supprimer le relevé bancaire
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Êtes-vous sûr de vouloir supprimer ce
                                          relevé bancaire ? Cette action est
                                          irréversible.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>
                                          Annuler
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() =>
                                            handleDeleteBankStatement(
                                              statement.id,
                                            )
                                          }
                                          className="bg-destructive hover:bg-destructive/90"
                                        >
                                          Supprimer
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              );
                            })}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderBankStatementDocuments()}

      {/* Bank statements upload section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Télécharger les relevés bancaires
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={isLoadingDocuments}
            >
              <RefreshCw
                className={`h-3 w-3 ${isLoadingDocuments ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <input
              ref={docsInputRef}
              type="file"
              multiple
              accept=".pdf,image/*"
              className="hidden"
              onChange={onDocsSelect}
            />
            <Button
              type="button"
              className="h-10 flex-1"
              onClick={() => docsInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Sélectionner les relevés bancaires
            </Button>
            <Button
              type="button"
              onClick={uploadDocuments}
              disabled={docsCount === 0 || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Téléchargement...
                </>
              ) : (
                "Télécharger"
              )}
            </Button>
          </div>
          {docsCount > 0 && (
            <div className="p-3 bg-muted rounded-md">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {docsCount} fichier(s) sélectionné(s)
                </span>
              </div>
              {isUploading ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-muted-foreground">
                    Téléchargement en cours...
                  </div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground mt-1">
                  Prêt pour téléchargement
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BankStatements;
